import React, { useEffect, useState, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Facebook, Instagram, Twitter, WhatsApp } from '@mui/icons-material';
export default function ResponsiveSlider() {
  const { t } = useTranslation();
  const direction = i18next.dir();
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef(null);
  const slides = t('slides', { returnObjects: true });

  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
  const startAutoPlay = () => { intervalRef.current = setInterval(nextSlide, 5000); };
  const stopAutoPlay = () => clearInterval(intervalRef.current);
  const resetAutoPlay = () => { stopAutoPlay(); startAutoPlay(); };

  const scrollToThumbnail = () => {
    const container = document.querySelector('.thumbnail');
    const activeThumb = document.getElementById(`thumb-${activeIndex}`);
    if (container && activeThumb) {
      const containerRect = container.getBoundingClientRect();
      const thumbRect = activeThumb.getBoundingClientRect();
      if (thumbRect.left < containerRect.left) {
        container.scrollBy({ left: thumbRect.left - containerRect.left - 10, behavior: 'smooth' });
      } else if (thumbRect.right > containerRect.right) {
        container.scrollBy({ left: thumbRect.right - containerRect.right + 10, behavior: 'smooth' });
      }
    }
  };

  const scrollThumbnailBy = (amount) => {
    const container = document.querySelector('.thumbnail');
    if (container) container.scrollBy({ left: amount, behavior: 'smooth' });
  };

  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, []);

  useEffect(() => {
    scrollToThumbnail();
    resetAutoPlay();
  }, [activeIndex]);

  return (
    <Box sx={{ height: { xs: '92vh', md: '95vh' }, position: 'relative', overflow: 'hidden' }}>
      {/* Main Slide Area */}
      <Box className="list" sx={{ height: '100%', position: 'relative' }}>
        {slides.map((slide, index) => (
          <Box
            key={index}
            sx={{
              backgroundImage: `url(image/img${index + 1}.jpg)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'absolute',
              inset: 0,
              opacity: index === activeIndex ? 1 : 0,
              transition: 'opacity 0.5s ease',
              zIndex: index === activeIndex ? 10 : 1,
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0, 0, 0, 0.7)', zIndex: 1 }} />
            <Box
              sx={{
                position: 'absolute',
                left: direction === 'ltr' ? { xs: '5%', sm: '5%' } : 'auto',
                right: direction === 'rtl' ? { xs: '5%', sm: '5%' } : 'auto',
                top: { xs: '15%', sm: '10%' },
                width: { xs: '90%', sm: '500px' },
                color: 'white',
                zIndex: 2,
                textAlign: direction === 'rtl' ? 'right' : 'left',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 5,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.9rem' },
                  mb: 2,
                }}
              >
                {t('group')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '4rem', md: '6rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mb: 2,
                }}
              >
                {slide.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, lineHeight: 1.5 }}
              >
                {slide.description}
              </Typography>

              <Box
  sx={{
    display: 'flex',
    gap: 2,
    mt: 3,
    alignItems: 'center',
  }}
>
  {[
    { icon: <Instagram />, href: 'https://www.instagram.com/luluatalomran/', color: '#E4405F' },
    { icon: <Facebook />, href: 'https://www.facebook.com', color: '#1877F2' },
    { icon: <Twitter />, href: 'https://x.com/luluatalomran', color: '#1DA1F2' },
    { icon: <WhatsApp />, href: 'https://wa.me/1234567890', color: '#25D366' },
  ].map(({ icon, href, color }, i) => (
    <IconButton
      key={i}
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color,
        backgroundColor: 'white',
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg)',
        '&:hover': {
          transform: 'perspective(1000px) rotateX(5deg) rotateY(5deg) scale(1.15)',
          boxShadow: `0 6px 20px ${color}`,
        },
      }}
    >
      {icon}
    </IconButton>
  ))}
</Box>

            </Box>
          </Box>
        ))}
      </Box>

      {/* Thumbnails + Arrows (Fully Responsive) */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 20, md: 50 },
          left: 0,
          right: 0,
          zIndex: 12,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2, sm: 4 },
          overflow: 'hidden',
        }}
      >
        {/* Left Arrow */}
      
        {/* Thumbnails */}
        <Box
          className="thumbnail"
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            overflowX: 'auto',
            flexGrow: 1,
            maxWidth: '100%',
            scrollSnapType: 'x mandatory',
            boxSizing: 'border-box',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {slides.map((slide, index) => (
            <Box
              key={index}
              id={`thumb-${index}`}
              onClick={() => { setActiveIndex(index); resetAutoPlay(); }}
              component="li"
              sx={{
                scrollSnapAlign: 'center',
                border: index === activeIndex ? '3px solid white' : '2px solid white',
                borderRadius: 2,
                overflow: 'hidden',
                flexShrink: 0,
                cursor: 'pointer',
                width: { xs: 80, sm: 100, md: 200 },
                height: { xs: 50, sm: 70, md: 120 },
                transition: 'border 0.3s ease',
                boxShadow: index === activeIndex ? '0 0 10px 3px #0026ff' : '',
              }}
            >
              <Box
                component="img"
                src={`image/img${index + 1}.jpg`}
                alt={slide.title}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          ))}
        </Box>

        {/* Right Arrow */}
       
      </Box>
    </Box>
  );
}

const navBtnStyle = {
  bgcolor: 'rgba(255, 255, 255, 0.3)',
  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.6)' },
  color: 'white',
  width: { xs: 30, sm: 35, md: 40 },
  height: { xs: 30, sm: 35, md: 40 },
  flexShrink: 0,
};
